import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchReadiness, type Readiness } from '../api/catalog';
import { fetchPlayableServers, scanServerContent, type PlayableServer } from '../api/servers';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { LoadProblemNote } from './LoadProblemNote';
import { PageLoading } from './PageStates';
import { problemOf, type LoadProblem } from './loadProblem';
import { Details, QuickStart } from './NewMission';
import {
  DECIDE_KEY,
  START_KEY,
  STEP,
  patchStored,
  readDraft,
  writeDraft,
  type MissionDraftDecide,
  type MissionDraftStart,
} from './newMissionDraft';
import { shortServerName, usable } from './serverFit';
import { Shell } from './Shell';

/**
 * The quick start, step 1: the server the night will be played on.
 *
 * The server is the mod set. On the owner's panel the mods are edited on each
 * server — one server per kind of night: modern, WW2, Antistasi — and a preset
 * is a Launcher file imported once and never looked at again (2026-09-19). So
 * this step asks where the night is played, and everything after it — terrains,
 * factions, vehicles, bases — comes from the catalogue of what that server runs
 * now: its Workshop mods and its Creator DLCs.
 *
 * A mod set nobody has read yet has no catalogue, and nothing can be offered
 * from it: the step says so and a server manager starts the scan from here. It
 * runs on a throwaway server of the platform's own, never on the one chosen.
 * Only servers the platform may start a session on are offered.
 *
 * The list is the one whoever makes missions may read
 * (`GET /api/servers/playable`), not the servers page's, which is a server
 * manager's. And a list that did not arrive is not an empty one: a refusal
 * says this account may not see the servers and whom to ask, any other failure
 * that they could not be read, and only a list that came back empty says the
 * platform has no server to use (2026-10-11: a refusal read as "no server").
 */
export function NewMissionServer() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [servers, setServers] = useState<PlayableServer[] | null>(null);
  const [problem, setProblem] = useState<LoadProblem | null>(null);
  const [asked, setAsked] = useState(0);
  const [serverId, setServerId] = useState<number | null>(() => readDraft().serverId ?? null);
  const [ready, setReady] = useState<Readiness | null>(null);
  // The era is the mission's: the server proposes one and says which others it can carry.
  const [era, setEra] = useState<string | null>(() => readDraft().era ?? null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let live = true;
    setProblem(null);
    fetchPlayableServers()
      .then((r) => live && setServers(r.servers))
      .catch((err) => live && setProblem(problemOf(err)));
    return () => {
      live = false;
    };
  }, [asked]);

  const server = servers?.find((s) => s.id === serverId) ?? null;
  const eras = server?.setting?.eras ?? [];
  const chosenEra = era && eras.includes(era) ? era : (server?.setting?.defaultEra ?? null);

  /** What the server runs and whether it is read; asked again while a scan runs. */
  const ask = useCallback((id: number) => {
    if (poll.current) clearTimeout(poll.current);
    fetchReadiness(id)
      .then((r) => {
        setReady(r);
        if (r.scanning && !r.scanned) poll.current = setTimeout(() => ask(id), 10_000);
      })
      .catch(() => setReady(null));
  }, []);

  useEffect(() => {
    setReady(null);
    setError(null);
    if (serverId != null) ask(serverId);
    return () => {
      if (poll.current) clearTimeout(poll.current);
    };
  }, [serverId, ask]);

  async function scan() {
    if (!server) return;
    setAsking(true);
    setError(null);
    try {
      const started = await scanServerContent(server.id);
      // A scan that could not start is answered, not thrown: say why.
      if (started.status === 'FAILED') setError(t.scanFailed(started.error ?? ''));
      ask(server.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAsking(false);
    }
  }

  function next() {
    if (!server || !ready?.scanned) return;
    const before = readDraft();
    // A different mod set invalidates everything that names one of its classes:
    // the map and the enemy come out of its catalogue, and so do our faction,
    // the squad's unit classes and loadouts, the vehicles, the enemy's defences
    // and the base (F10). Another server with the same mods changes none of it.
    // Another era is another night: other factions, other bases, other ways in.
    const changed = before.modSetHash !== ready.modSetHash || (before.era ?? null) !== chosenEra;
    writeDraft({
      serverId: server.id,
      serverName: server.name,
      modSetHash: ready.modSetHash,
      era: chosenEra ?? undefined,
      supportsSystem: ready.supportsSystem ?? 'vanilla',
      ...(changed
        ? {
            worldName: undefined,
            worldDisplayName: undefined,
            enemyProfileId: null,
            enemyFactionClass: null,
            enemyName: null,
            preferredPlace: null,
            enemyDefences: null,
            playerFactionClass: null,
            playerFactionName: null,
            playerProfileId: null,
            roles: null,
            aircrew: null,
          }
        : {}),
    });
    if (changed) {
      // The supports were counted for the old mod set's system, and the vehicles
      // at the base are its classes.
      patchStored<MissionDraftDecide>(DECIDE_KEY, { supports: null });
      patchStored<MissionDraftStart>(START_KEY, { vehicles: [], vehiclesFor: undefined });
    }
    navigate('/new/type');
  }

  const t = ca.missionUi.server;
  // Running first, then stopped; the sort is stable, so the panel's own order holds inside each.
  const canPlay = (servers ?? [])
    .filter(usable)
    .sort((a, b) => Number(b.running) - Number(a.running));
  const unlinked = (servers ?? []).filter((s) => !usable(s));
  const mayScan = me.roles.includes('ADMIN') || me.roles.includes('SERVER_MANAGER');

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <button
          type="button"
          className="primary"
          disabled={!server || !ready?.scanned}
          onClick={next}
        >
          {ca.next}
        </button>
      }
    >
      <QuickStart
        step={STEP.server}
        aside={
          <Details>
            {!server ? (
              <p className="muted small">{ca.missionUi.details.pickFirst}</p>
            ) : (
              <>
                <p>
                  <strong>{shortServerName(server.name)}</strong>
                </p>
                <p className="muted small">
                  {[
                    t.summaryMods(server.activeMods?.length ?? 0),
                    ...(ready ? [t.summaryDlcs(ready.dlcs?.length ?? 0)] : []),
                    ...(ready
                      ? [
                          ready.supportsSystem === 'simplex'
                            ? ca.missionUi.supportsSystem.simplex
                            : ca.missionUi.supportsSystem.vanilla,
                        ]
                      : []),
                  ].join(' · ')}
                </p>
                {(ready?.dlcs?.length ?? 0) > 0 && (
                  <p className="muted small">
                    {t.dlcs} {ready!.dlcs!.join(', ')}
                  </p>
                )}
                {ready && (
                  <>
                    <p className={ready.scanned ? 'muted small' : 'small warn'}>
                      {ready.scanned ? t.scanned : ready.scanning ? t.scanning : t.notScanned}
                    </p>
                    {!ready.scanned && !ready.scanning && (
                      <>
                        {mayScan ? (
                          <button type="button" disabled={asking} onClick={() => void scan()}>
                            {asking ? t.scanStarting : t.scan}
                          </button>
                        ) : (
                          <p className="muted small">{t.scanAsk}</p>
                        )}
                        <p className="muted small">{t.scanHint}</p>
                      </>
                    )}
                    <p className="muted small">
                      {ca.missionUi.details.supportsSystemLabel}{' '}
                      <span className="chip">
                        {ready.supportsSystem === 'simplex'
                          ? ca.missionUi.supportsSystem.simplex
                          : ca.missionUi.supportsSystem.vanilla}
                      </span>
                    </p>
                  </>
                )}
                {(server.activeMods?.length ?? 0) > 0 && (
                  <details className="fold">
                    <summary className="muted small">{t.showMods}</summary>
                    <ul className="chips">
                      {(server.activeMods ?? []).slice(0, 24).map((m) => (
                        <li key={m.id} className="chip">
                          {m.name}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {error && <p className="small warn">{error}</p>}
              </>
            )}
          </Details>
        }
      >
        <section className="card form" aria-label={t.title}>
          <h2>{t.title}</h2>
          <p className="muted small">{t.blurb}</p>
          <div className="cards grid pick servers" role="radiogroup" aria-label={t.title}>
            {canPlay.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`card ${serverId === s.id ? 'selected' : ''}`}
                role="radio"
                aria-checked={serverId === s.id}
                aria-label={shortServerName(s.name)}
                title={s.name}
                onClick={() => setServerId(s.id)}
              >
                <strong>{shortServerName(s.name)}</strong>
                <span className="muted small status-line">
                  <span className={`dot ${s.running ? 'ok' : 'off'}`} aria-hidden="true" />
                  {ca.access[s.access]} · {s.running ? t.running : ca.missionUi.when.serverOff}
                  {s.scanned === false ? ` · ${t.notScannedShort}` : ''}
                </span>
              </button>
            ))}
          </div>
          {unlinked.length > 0 && (
            <details className="fold">
              <summary className="muted small">{t.moreToLink(unlinked.length)}</summary>
              <div className="cards grid pick servers" role="radiogroup" aria-label={t.title}>
                {unlinked.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    className="card lacks"
                    role="radio"
                    aria-checked={false}
                    aria-disabled
                    aria-label={shortServerName(s.name)}
                    title={`${s.name} · ${ca.missionUi.page.serverNotLinked}`}
                  >
                    <strong>{shortServerName(s.name)}</strong>
                    <span className="muted small">{ca.missionUi.page.serverNotLinked}</span>
                  </button>
                ))}
              </div>
            </details>
          )}
          {problem ? (
            <LoadProblemNote
              problem={problem}
              what="servers"
              onRetry={() => setAsked((n) => n + 1)}
            />
          ) : servers === null ? (
            <PageLoading cards={2} />
          ) : (
            canPlay.length === 0 && <p className="small warn">{t.none}</p>
          )}
          {problem !== 'forbidden' && (
            <p className="muted small">
              {t.missing}{' '}
              {mayScan ? (
                <Link to="/servers">{ca.missionUi.when.serversLinkThem}</Link>
              ) : (
                t.missingAsk
              )}
            </p>
          )}
          {eras.length > 1 && (
            <label>
              {t.era}
              <select name="era" value={chosenEra ?? ''} onChange={(e) => setEra(e.target.value)}>
                {eras.map((e) => (
                  <option key={e} value={e}>
                    {ca.eras[e] ?? e}
                  </option>
                ))}
              </select>
              <span className="muted small">{t.eraHint}</span>
            </label>
          )}
        </section>
      </QuickStart>
    </Shell>
  );
}
